import { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { styled } from "~/application/theme";
import { Flex } from "../Flex";
import { Icon } from "../Icon";
import { TabsContext } from "../Tabs/contexts/TabsContext";
import { useUser } from "~/presentation/core/contexts/UserContext";
import { TABS_CONFIG, TabConfig } from "./types";
import { TabItem } from "./components/TabItem";
import { useTabBarVisibility } from "./hooks/useTabBarVisibility";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItems,
  DropdownMenuTrigger,
} from "~/application/components/DropdownMenu";
import { NavLink } from "../Link";

const Bar = styled("nav", {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  background: "#fff",
  boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  height: "72px",
  padding: "0 8px",
});

export const AppTabBar = () => {
  const { activeValue, setActiveValue } = useContext(TabsContext);
  const { contexts } = useUser();
  const location = useLocation();
  const isVisible = useTabBarVisibility();

  useEffect(() => {
    const foundTab = TABS_CONFIG.find((tab) => {
      if (tab?.match?.test(location.pathname)) return true;

      return tab.menuItems?.some((item) => item.match?.test(location.pathname));
    });

    const isMenu = location.pathname === "/menu" || location.pathname.includes("menu-opcoes");

    if (foundTab && activeValue !== foundTab.value) {
      setActiveValue(foundTab.value);
    } else if (isMenu && activeValue !== "menu") {
      setActiveValue("menu");
    }
  }, [location.pathname, activeValue, setActiveValue]);

  const handleTabClick = (value: string) => {
    setActiveValue(value);
    window.location.hash = value;
  };

  if (!isVisible) return null;

  const renderPopoverTab = (tab: TabConfig, isActive: boolean) => {
    const menuItems =
      tab.menuItems?.filter((item) => (item.shouldShow ? item.shouldShow(contexts) : true)) || [];

    if (menuItems.length === 0) {
      return (
        <Link to={tab.to} key={tab.value} style={{ textDecoration: "none" }}>
          <TabItem
            icon={tab.icon}
            label={tab.label}
            isActive={isActive}
            onClick={() => handleTabClick(tab.value)}
          />
        </Link>
      );
    }

    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <TabItem icon={tab.icon} label={tab.label} isActive={isActive} />
        </DropdownMenuTrigger>

        <DropdownMenuContent>
          <DropdownMenuItems>
            {menuItems.map((option) => (
              <NavLink key={option.to} to={option.to} end>
                {({ isActive }) => (
                  <DropdownMenuItem
                    data-active={isActive}
                    onClick={() => handleTabClick(tab.value)}
                  >
                    <Icon as={option.icon} size="md" />
                    {option.label}
                  </DropdownMenuItem>
                )}
              </NavLink>
            ))}
          </DropdownMenuItems>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <Bar>
      <Flex
        css={{
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        {TABS_CONFIG.map((tab) => {
          if (tab.shouldShow && !tab.shouldShow(contexts)) return null;

          const isActive = activeValue === tab.value;
          const isPopover = tab.usePopover && tab.usePopover(contexts);

          if (isPopover) {
            return renderPopoverTab(tab, isActive);
          }

          return (
            <Link key={tab.value} to={tab.to} style={{ textDecoration: "none" }}>
              <TabItem
                icon={tab.icon}
                label={tab.label}
                isActive={isActive}
                onClick={() => handleTabClick(tab.value)}
              />
            </Link>
          );
        })}
      </Flex>
    </Bar>
  );
};
